import React, { useCallback, useEffect, useState } from "react";
import { useHistory, withRouter } from "react-router-dom";
import classNames from "../../../core/utils/class-names";
import ItemWithSub from "./ItemWithSub";
import SimpleItem from "./SimpleItem";
import "./Menu.scss";
import usePermissions from "../../../core/hooks/usePermissions";
import { treeMenuType } from "../../../core/enums/treeMenuType";
import { isMenuAllowed } from "../../../core/utils/allowedMenus";
import isEmpty from "../../../core/helpers/isEmpty";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { SPRINT_VERSION } from "../../../core/constants/termsCondition";
import Logo from "../logo/logo";
import LogoPng from "../../../assets/img/whiteLogo.png";
import { Collapse } from "antd";
import { SimpleDownArrowSvg } from "../icons/iconsSvg";
import { useDispatch, useSelector } from "react-redux";
import { sidebarExpandedKeys } from "../../../redux/rootView/actions";
import SettingsDropdown from "../actions/SettingsDropdown";
import SupportLinksDropdown from "../actions/SupportLinksDropdown";
import localize from "../../../core/utils/translation-files";
const haveActive = (sub, route) => {
    return Array.isArray(sub)
        ? // eslint-disable-next-line @typescript-eslint/no-unused-vars
            sub.some((item) => item.routing === route || haveActive(item.sub, route))
        : false;
};
const Menu = ({ data, orientation, location, children, className, onCloseSidebar, opened, isAdmin, toggleSidebar, resetSideBar, }) => {
    const history = useHistory();
    const [menu, setMenu] = useState([]);
    const [renderKey, setRenderKey] = useState(0);
    const dispatch = useDispatch();
    const menuKeys = useSelector((state) => { var _a; return (_a = state.rootView) === null || _a === void 0 ? void 0 : _a.sidebarExpandedKeys; });
    const { isSingleGroup, isSingleCompany, groupOptions } = usePermissions(true);
    const mapMenuItems = useCallback((items, currentRoute) => {
        var _a;
        const mapNode = (node) => {
            var _a;
            // 1) NEW: handle group wrappers like { title, children: [...] }
            if (Array.isArray(node === null || node === void 0 ? void 0 : node.children) && node.children.length) {
                const mappedChildren = node.children.map(mapNode);
                // Active if any child is active (directly or deep)
                const anyChildActive = mappedChildren.some((c) => c === null || c === void 0 ? void 0 : c.active) ||
                    mappedChildren.some((c) => haveActive(c === null || c === void 0 ? void 0 : c.sub, currentRoute)) ||
                    mappedChildren.some((c) => haveActive(c === null || c === void 0 ? void 0 : c.children, currentRoute));
                // Keep group wrapper expanded when something inside is active
                return Object.assign(Object.assign({}, node), { active: false, status: anyChildActive ? treeMenuType.expanded : ((_a = node.status) !== null && _a !== void 0 ? _a : treeMenuType.collapse), children: mappedChildren });
            }
            // 2) Existing: handle expandable menus using `sub`
            if (Array.isArray(node === null || node === void 0 ? void 0 : node.sub) && node.sub.length) {
                let updateChildStatus = treeMenuType.expandable;
                if ((node.routing === "groups" && !isSingleGroup) ||
                    (node.routing === "companies" && !isSingleCompany)) {
                    updateChildStatus = treeMenuType.collapse;
                }
                return Object.assign(Object.assign({}, node), { active: node.routing === currentRoute, status: haveActive(node.sub, currentRoute)
                        ? treeMenuType.expanded
                        : updateChildStatus, sub: node.sub.map(mapNode) });
            }
            // 3) Leaf item
            return Object.assign(Object.assign({}, node), { active: node.routing === currentRoute });
        };
        return (_a = items === null || items === void 0 ? void 0 : items.map(mapNode)) !== null && _a !== void 0 ? _a : [];
    }, 
    // include these if used inside
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSingleGroup, isSingleCompany, data, groupOptions]);
    const handleUpdateMenuStatus = (menu, menuTitle, status) => {
        var _a;
        const walk = (list) => list === null || list === void 0 ? void 0 : list.map((node) => {
            // ---- If this node is a WRAPPER that has children ----
            if (Array.isArray(node === null || node === void 0 ? void 0 : node.children) && node.children.length) {
                return Object.assign(Object.assign({}, node), { children: walk(node.children) });
            }
            // ---- If this node has sub (expandable tree item) ----
            if (Array.isArray(node === null || node === void 0 ? void 0 : node.sub) && node.sub.length) {
                let updateChildStatus = node.status;
                // your rule: when groups becomes expandable, companies handled specially
                if (menuTitle === "groups" && status === treeMenuType.expandable) {
                    updateChildStatus =
                        node.routing === "companies" && isSingleCompany
                            ? treeMenuType.expandable
                            : treeMenuType.collapse;
                }
                return Object.assign(Object.assign({}, node), { active: node.routing === menuTitle, status: node.routing === menuTitle ? status : updateChildStatus, sub: walk(node.sub) });
            }
            // ---- Leaf item ----
            return Object.assign(Object.assign({}, node), { active: node.routing === menuTitle });
        });
        return (_a = walk(menu)) !== null && _a !== void 0 ? _a : [];
    };
    useEffect(() => {
        var _a, _b;
        const currentRoute = location.pathname.split("/")[2];
        const updatedMenu = (_b = (_a = location === null || location === void 0 ? void 0 : location.state) === null || _a === void 0 ? void 0 : _a.updatedMenu) !== null && _b !== void 0 ? _b : (data ? mapMenuItems(data, currentRoute) : []);
        setMenu((prev) => updatedMenu);
        data === null || data === void 0 ? void 0 : data.map((item) => {
            var _a;
            (_a = item === null || item === void 0 ? void 0 : item.children) === null || _a === void 0 ? void 0 : _a.map((childItem) => {
                if ((childItem === null || childItem === void 0 ? void 0 : childItem.routing) === "sites" || (childItem === null || childItem === void 0 ? void 0 : childItem.routing) === "companies") {
                    dispatch(sidebarExpandedKeys([...menuKeys, localize(["DashboardMenus.administration"])]));
                }
                if ((childItem === null || childItem === void 0 ? void 0 : childItem.routing) === currentRoute) {
                    if (!menuKeys.includes(item.title))
                        dispatch(sidebarExpandedKeys([...menuKeys, item.title]));
                }
            });
        });
    }, [location, data, mapMenuItems]);
    useEffect(() => {
        onCloseSidebar && opened && onCloseSidebar();
    }, [location, onCloseSidebar, opened]);
    const handleItemClick = (itemTitle, layout, parentRoute, status) => {
        const currentRoute = location.pathname.split("/")[2];
        // ✅ IMPORTANT: if user clicked the already-active route, do NOTHING
        if (parentRoute === currentRoute) {
            return;
        }
        const updateMenu = [...menu];
        const updateStatus = status === treeMenuType.expandable
            ? treeMenuType.expanded
            : treeMenuType.expandable;
        const updatedMenu = handleUpdateMenuStatus(updateMenu, parentRoute, updateStatus);
        setMenu(updatedMenu);
        setRenderKey(Math.random());
        if (!isEmpty(parentRoute)) {
            history.push(`/${layout}/${parentRoute}`, { updatedMenu });
        }
    };
    const { Panel } = Collapse;
    const menuClasses = classNames({
        "main-menu": true,
        horizontal: orientation === "horizontal",
    });
    const menuItems = menu.map((item, i) => {
        var _a, _b;
        if (item === null || item === void 0 ? void 0 : item.children) {
            return (opened ?
                React.createElement(Collapse, { ghost: true, onChange: (key) => {
                        dispatch(sidebarExpandedKeys(key));
                    }, key: i, expandIcon: ({ isActive }) => React.createElement("span", { style: { display: "flex", alignItems: "center", justifyContent: "center", transition: "0.2s linear", transform: isActive ? "rotate(0deg)" : "rotate(-90deg)" } },
                        React.createElement(SimpleDownArrowSvg, null)), activeKey: menuKeys, 
                    //  defaultActiveKey={menuKeys}
                    bordered: false, destroyInactivePanel: true, className: "menu-collapse" },
                    React.createElement(Panel, { header: item.title, key: item.title }, (_a = item === null || item === void 0 ? void 0 : item.children) === null || _a === void 0 ? void 0 : _a.map((childItem, j) => {
                        if (childItem === null || childItem === void 0 ? void 0 : childItem.sub)
                            return (React.createElement(ItemWithSub, { key: childItem.title + j, layout: orientation, sub: childItem.sub, title: childItem.title, location: location, active: childItem.active, icon: childItem.icon, onClick: handleItemClick, status: childItem === null || childItem === void 0 ? void 0 : childItem.status, parentRoute: childItem === null || childItem === void 0 ? void 0 : childItem.routing, requiredPermissions: childItem === null || childItem === void 0 ? void 0 : childItem.requiredPermissions, isParent: false, expanded: opened, svgIcon: childItem === null || childItem === void 0 ? void 0 : childItem.svgIcon, event: childItem === null || childItem === void 0 ? void 0 : childItem.event }));
                        if (isMenuAllowed(childItem.requiredPermissions) === true)
                            return (React.createElement(SimpleItem, { key: j, icon: childItem.icon, layout: orientation, title: childItem.title, routing: childItem.routing, isAdmin: isAdmin, adminTitle: childItem.adminTitle, child: childItem.child, show: childItem.show, expanded: opened, resetSideBar: resetSideBar, svgIcon: childItem.svgIcon, event: childItem === null || childItem === void 0 ? void 0 : childItem.event, onClick: handleItemClick, status: childItem === null || childItem === void 0 ? void 0 : childItem.status }));
                    })))
                :
                    (_b = item === null || item === void 0 ? void 0 : item.children) === null || _b === void 0 ? void 0 : _b.map((childItem, j) => {
                        if (childItem === null || childItem === void 0 ? void 0 : childItem.sub)
                            return (React.createElement(ItemWithSub, { key: childItem.title + j, layout: orientation, sub: childItem.sub, title: childItem.title, location: location, active: childItem.active, icon: childItem.icon, onClick: handleItemClick, status: childItem === null || childItem === void 0 ? void 0 : childItem.status, parentRoute: childItem === null || childItem === void 0 ? void 0 : childItem.routing, requiredPermissions: childItem === null || childItem === void 0 ? void 0 : childItem.requiredPermissions, isParent: false, expanded: opened, svgIcon: childItem === null || childItem === void 0 ? void 0 : childItem.svgIcon, event: childItem === null || childItem === void 0 ? void 0 : childItem.event }));
                        if (isMenuAllowed(childItem.requiredPermissions) === true)
                            return (React.createElement(SimpleItem, { key: j, icon: childItem.icon, layout: orientation, title: childItem.title, routing: childItem.routing, isAdmin: isAdmin, adminTitle: childItem.adminTitle, child: childItem.child, show: childItem.show, expanded: opened, resetSideBar: resetSideBar, svgIcon: childItem.svgIcon, event: childItem === null || childItem === void 0 ? void 0 : childItem.event, onClick: handleItemClick, status: childItem === null || childItem === void 0 ? void 0 : childItem.status }));
                    }));
        }
    });
    // Assuming menuItems is an array containing your menu items
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: `${menuClasses} ${!!className && className}`, key: renderKey },
            children,
            React.createElement("span", { className: "navbar-toggle", onClick: toggleSidebar, style: { alignItems: opened ? `flex-end` : `center` } }, opened ? (React.createElement(React.Fragment, null,
                React.createElement(Logo, { src: LogoPng, className: "menuDelphiLogo" }),
                React.createElement(MenuFoldOutlined, { className: "menuFoldOutlineIcon" }))) : (React.createElement(MenuUnfoldOutlined, null))),
            !!menuItems.length && (React.createElement("nav", { className: "main-menu-wrap", id: "main-menu-wrap" },
                React.createElement("ul", { className: "menu-ul" }, menuItems))),
            React.createElement(SupportLinksDropdown, null),
            React.createElement(SettingsDropdown, null),
            React.createElement("div", { className: "navbar-version-text" },
                "Version ",
                SPRINT_VERSION))));
};
export default withRouter(Menu);
